import i18n from 'i18next';
import ChainedBackend, { type ChainedBackendOptions } from 'i18next-chained-backend';
import Backend from 'i18next-http-backend';
import LocalStorageBackend from 'i18next-localstorage-backend';
import { initReactI18next } from 'react-i18next';
import { clearLocaleAndReload, getLocaleInfo, setLocaleAndReload } from './utils/locale-cookie';
import { getLanguageWithLocaleOverride, getMarket, getMarketCode } from './utils/market';

// NEXT_PUBLIC_LOCALES_VERSION is auto-generated from locale files hash in next.config.ts
// In dev: changes when you restart the server (picks up locale file changes)
// In prod: changes on new build when locale files change
const localesVersion = process.env.NEXT_PUBLIC_LOCALES_VERSION || 'dev';

const currentMarket = getMarket();
// Check for locale cookie override before setting initial language
const marketCode = currentMarket?.marketCode || getMarketCode();
const initialLocale = getLanguageWithLocaleOverride(marketCode);

// Attach locale helper methods to window for easy access in browser console
if (typeof window !== 'undefined') {
  window.setLocale = setLocaleAndReload;
  window.clearLocale = clearLocaleAndReload;
  window.getLocaleInfo = getLocaleInfo;
}

i18n
  .use(ChainedBackend)
  .use(initReactI18next)
  .init<ChainedBackendOptions>({
    backend: {
      backends: [LocalStorageBackend, Backend],
      backendOptions: [
        {
          // In dev: cache until server restart (Date.now changes on restart)
          // In prod: cache until new deployment (LOCALES_HASH changes on build)
          expirationTime: 7 * 24 * 60 * 60 * 1000, // 7 days
          defaultVersion: localesVersion,
        },
        {
          loadPath: `/locales/{{lng}}/{{ns}}.json?v=${localesVersion}`,
        },
      ],
    },
    nsSeparator: false,
    debug: false,
    ns: [
      'activity',
      'authentication',
      'common',
      'glossary',
      'guide',
      'header',
      'helpAndSupport',
      'leads',
      'mainNavigation',
      'mainNavigationV2',
      'other',
      'profile',
      'settings',
      'settingsAccessories',
      'settingsBankId',
      'settingsEcom',
      'settingsExportFiltersBlocket',
      'settingsExportFiltersFinn',
      'settingsFinancial',
      'settingsInsurance',
      'settingsLeasing',
      'settingsPayment',
      'settingsPromos',
      'settingsResellerPackages',
      'termsAndConditions',
      'validation',
      'salesAndPurchasing',
      'salesAndPurchasingCentralStorage',
      'salesAndPurchasingVehicles',
      'salesAndPurchasingVehiclesItem',
      'salesAndPurchasingLeads',
      'salesAndPurchasingMarketing',
      'salesAndPurchasingMessages',
      'salesAndPurchasingOnlineOrders',
      'salesAndPurchasingAssignments',
      'salesAndPurchasingValuation',
      'retailerInformation',
      'retailerInformationUsers',
      'retailerInformationDefaultContactPerson',
      'retailerInformationAboutUs',
      'retailerInformationOpeningHours',
      'retailerInformationDepartments',
      'retailerInformationVehicleLocation',
      'processSettings',
      'retailerInformationAssignees',
      'processSettingsProcesses',
      'processSettingsProcessSteps',
      'processSettingsRoles',
      'integrations',
      'integrationsBus',
      'integrationsImageImport',
      'integrationsManufacturerData',
      'integrationsWebhooks',
      'website',
      'websiteCampaigns',
      'websiteContacts',
      'websiteForms',
      'websiteMediaBank',
      'websiteModules',
      'websitePages',
      'websiteSettings',
      'pfPurchaseCalculation',
      'settingsCommercePackaging',
      'internalCarsAssessmentItem',
      'internalCarsWorkOrderItem',
      'internalCarsWorkStateReportItem',
      'processSettingsOperationsObservations',
      'processSettingsStationsOperations',
      'settingsServices',
      'operations',
      'workOperationItemState',
      'operationObservation',
      'workOrderList',
      'pagedWorkOrders',
      'pagedStations',
      'snackbar',
      'externalOperationsNavigation',
      'operationsStations',
      'operationsStatistics',
      'createVehiclePanel',
      'unsavedChanges',
      'processFlowFormObservationAction',
      'logisticsAppointmentTemplate',
      'logisticsAppointmentTemplates',
      'webhooks',
      'workorderItemNotes',
      'workorderItemSubDocuments',
      'workorderItemSubNotes',
      'operationsRoles',
      'integrationsVideoImport',
      'processFlowBusStateReport',
    ],
    defaultNS: 'common',
    lng: initialLocale,
    fallbackLng: 'en-US',
    load: 'currentOnly',
    interpolation: {
      escapeValue: false,
    },
  });
